'use client'

import { CommunicateWithUs } from '@/components/CommunicateWithUs'
import { Button } from '@/components/fun-ui/Button'
import { useBrowser } from '@/hooks/useBrowser'
import { useTrace } from '@/hooks/useTrace'
import Image from 'next/image'
import { useEffect, useRef } from 'react'

interface ErrorComponentProps {
  error: Error & { digest?: string }
  reset: () => void
}

// 错误记录去重缓存：存储错误标识和最后记录时间
const errorRecordCache = new Map<string, number>();
const DEBOUNCE_TIME = 60 * 1000; // 1分钟（毫秒）

// 生成错误唯一标识
function getErrorKey(error: Error & { digest?: string }): string {
  // 使用 message 和 digest 作为唯一标识
  return `${error.message}|${error.digest || ''}`;
}

// 清理过期的错误记录
function cleanupExpiredRecords() {
  const now = Date.now();
  const keysToDelete: string[] = [];
  errorRecordCache.forEach((timestamp, key) => {
    if (now - timestamp > DEBOUNCE_TIME) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(key => errorRecordCache.delete(key));
}

// 检查错误是否在去重时间窗口内
function shouldRecordError(error: Error & { digest?: string }): boolean {
  cleanupExpiredRecords();
  const errorKey = getErrorKey(error);
  const lastRecordTime = errorRecordCache.get(errorKey);
  const now = Date.now();

  if (lastRecordTime && now - lastRecordTime < DEBOUNCE_TIME) {
    // 在1分钟内已记录过相同错误，跳过
    return false;
  }

  // 记录本次错误时间
  errorRecordCache.set(errorKey, now);
  return true;
}

export default function Error({ error, reset }: ErrorComponentProps) {
  const { error: traceError } = useTrace();
  const browser = useBrowser();
  const pkg = process.env.NEXT_PUBLIC_PACKAGE;

  // 记录错误到错误监控系统
  useEffect(() => {
    if (pkg !== 'local' && browser) {
      // 检查是否需要记录（去重逻辑）
      if (!shouldRecordError(error)) {
        return;
      }

      traceError({
        error: {
          message: error.message,
          stack: error.stack,
          digest: error.digest,
        },
        browser: `系统：${browser.getOSName()}@${browser.getOSVersion()};浏览器：${browser.getBrowserName()}@${browser.getBrowserVersion()};引擎：${browser.getEngineName()}userAgent：${window.navigator.userAgent}`,
        page: {
          url: window.location.href,
          type: 'page_error',
        },
        timestamp: new Date().toISOString(),
      });
    }
  }, [error, pkg, browser, traceError])

  return (
    <div className="flex flex-col h-screen w-screen items-center justify-center gap-4">
      <Image src="https://oss.findinai.cn/storage/20250813/427fbb9f3070adb12222d3139d5ea928.svg" width={150} height={150} alt="错误提示" />
      <span className="text-annotation-foreground text-sm">诶呀，遇到了一个错误，再重新试试！{error?.message?.includes('Loading chunk') && (
        <>
          推荐<a
            className="text-annotation-foreground text-sm underline underline-offset-4 cursor-pointer"
            href="https://www.google.cn/chrome/index.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            使用 Chrome 浏览器
          </a>
          访问。
        </>
      )}</span>
      <div className="flex items-center gap-4">
        <Button
          onClick={reset}
          className="min-w-20"
        >
          重试
        </Button>
        <Button
          variant="secondary"
          className="min-w-20"
          onClick={() => window.location.assign('/')}
        >
          返回首页
        </Button>
      </div>
      <div className="absolute bottom-5 flex items-center cursor-pointer">
        <CommunicateWithUs>
          <span className="text-annotation-foreground text-sm">无法解决？联系官方小助手 &gt;</span>
        </CommunicateWithUs>
      </div>
    </div>
  )
}